import type { Rule } from 'antd/es/form';
import { money } from './format';

/**
 * Blocks a form from submitting when the entered amount is larger than what is actually available,
 * and names the exact figure so it can be corrected in one step.
 *
 * This is a hard stop rather than a warning: the advisory alert on the withdrawal forms told the
 * user they were over but still let the form through, which is how a profit payment larger than the
 * profit earned reached the ledger and left a negative balance. antd runs field validators on
 * submit, so rejecting here prevents the mutation from ever firing.
 *
 * The server enforces the same limits independently — this only stops the round trip and gives a
 * clearer message than the API's generic rejection.
 *
 * @param available what may be drawn; null/undefined while the figure is still loading, in which
 *                  case the rule stays quiet rather than blocking on data it does not have yet
 * @param label     how to name that figure in the error, e.g. "remaining investment"
 */
export function maxAmountRule(available: number | null | undefined, label: string): Rule {
  return {
    validator: (_, value: number) => {
      if (value == null || available == null) return Promise.resolve();

      if (value > available) {
        return Promise.reject(
          new Error(
            `Unable to process — PKR ${money(value)} is more than the ${label} of ` +
              `PKR ${money(available)}. Enter PKR ${money(available)} or less.`,
          ),
        );
      }

      return Promise.resolve();
    },
  };
}
